import { zodResolver } from '@hookform/resolvers/zod';
import {
  rolesControllerAssignPermissions,
  rolesControllerCreate,
  licensingAuthority as LicensingAuthority,
} from '@kalba/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Bell,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  FileText,
  FlaskConical,
  Info,
  Lock,
  MapPin,
  Search,
  Settings,
  Shield,
  Sparkles,
  User,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb } from '../../../components/layout/breadcrumb';
import { Button } from '../../../components/ui/button';
import { Tabs } from '../../../components/ui/tabs';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';
import { useRoleFormStore } from '../../../stores/role-form.store';

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/users/roles/new')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('role:create')) throw redirect({ to: '/dashboard' });
  },
  component: NewRolePage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Permission {
  id: string;
  key: string;
  resource: string;
  action: string;
  field: string | null;
  description?: string | null;
}

// request-type, fee-component, document-type are used by both municipalities (or are
// shared/global master data), so they're not gated here at all.
const isSharjahPerm = (resource: string) => resource.startsWith('sm-');
const isKalbaPerm = (resource: string) =>
  ['permit', 'pledge', 'inspection', 'payment-gateway', 'pledge-config'].includes(resource);
// ── Resource config ───────────────────────────────────────────────────────────

const RESOURCE_META: Record<
  string,
  {
    label: string;
    icon: React.ElementType;
    iconBg: string;
    iconColor: string;
  }
> = {
  customer: {
    label: 'Customers',
    icon: Users,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  permit: { label: 'Permits', icon: FileText, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
  payment: { label: 'Payments', icon: CreditCard, iconBg: 'bg-sky-100', iconColor: 'text-sky-600' },
  notification: {
    label: 'Notifications',
    icon: Bell,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  user: { label: 'Users', icon: User, iconBg: 'bg-violet-100', iconColor: 'text-violet-600' },
  role: { label: 'Roles', icon: Shield, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  config: {
    label: 'Configuration',
    icon: Settings,
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
  },
  'request-type': {
    label: 'Request Types',
    icon: Settings,
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
  },
  'document-type': {
    label: 'Document Types',
    icon: FileText,
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
  'fee-component': {
    label: 'Fee Components',
    icon: CreditCard,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
  },
  'pledge-config': {
    label: 'Pledge Studies',
    icon: BookOpen,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
  },
  'sm-pledge-config': {
    label: 'SM Pledge Studies',
    icon: BookOpen,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
  },
  'payment-gateway': {
    label: 'Payment Gateway',
    icon: Zap,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  inspection: {
    label: 'Inspections',
    icon: ClipboardCheck,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  pledge: {
    label: 'Pledge Study',
    icon: BookOpen,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  survey: {
    label: 'Survey',
    icon: ClipboardList,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  system: {
    label: 'Payment Gateway',
    icon: Zap,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  'sm-study': {
    label: 'SM Study Requests',
    icon: BookOpen,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  'sm-site-visit': {
    label: 'SM Site Visits',
    icon: MapPin,
    iconBg: 'bg-lime-100',
    iconColor: 'text-lime-600',
  },
  'sm-lab': {
    label: 'SM Lab Requests',
    icon: FlaskConical,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  'sm-study-report': {
    label: 'SM Study Reports',
    icon: FileText,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
  'sm-extension': {
    label: 'SM Extensions',
    icon: Clock,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
};

const RESOURCE_TAB: Record<string, 'general' | 'kalba' | 'sharjah'> = {
  user: 'general',
  role: 'general',
  config: 'general',
  'request-type': 'general',
  'document-type': 'general',
  'fee-component': 'general',
  'payment-gateway': 'general',
  system: 'general',
  notification: 'general',
  survey: 'general',
  customer: 'general',
  permit: 'kalba',
  inspection: 'kalba',
  pledge: 'kalba',
  'sm-study': 'sharjah',
  'sm-site-visit': 'sharjah',
  'sm-lab': 'sharjah',
  'sm-study-report': 'sharjah',
  'sm-extension': 'sharjah',
  'sm-pledge-config': 'sharjah',
  'pledge-config': 'kalba',
};

function getResourceMeta(resource: string) {
  return (
    RESOURCE_META[resource] ?? {
      label: resource.charAt(0).toUpperCase() + resource.slice(1),
      icon: Settings,
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
    }
  );
}

function derivePermissionName(action: string, resource: string): string {
  const { label } = getResourceMeta(resource);
  return `${action.charAt(0).toUpperCase() + action.slice(1)} ${label}`;
}

// ── Fetch all permissions (direct fetch to avoid client regen) ────────────────

async function fetchAllPermissions(): Promise<Permission[]> {
  const token = useAuthStore.getState().token;
  const res = await fetch('/api/v1/users/roles/permissions', {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load permissions');
  return res.json();
}

// ── Custom checkbox ───────────────────────────────────────────────────────────

function PermCheckbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        'h-5 w-5 shrink-0 rounded-[5px] border-2 flex items-center justify-center transition-all duration-150',
        disabled
          ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
          : checked
            ? 'bg-success-500 border-success-500'
            : 'bg-white border-gray-300 hover:border-gray-400',
      )}
    >
      {checked && <Check size={11} strokeWidth={3} className="text-white" />}
    </button>
  );
}

// ── Permission row ────────────────────────────────────────────────────────────

function PermRow({
  perm,
  checked,
  onToggle,
  disabled,
}: {
  perm: Permission;
  checked: boolean;
  onToggle: (id: string, v: boolean) => void;
  disabled?: boolean;
}) {
  const name = derivePermissionName(perm.action, perm.resource);
  return (
    <div
      className={cn(
        'flex items-start gap-4 px-5 py-3.5 transition-colors',
        disabled
          ? 'bg-gray-50/50 opacity-60 cursor-not-allowed'
          : checked
            ? 'bg-success-50/50 cursor-pointer hover:bg-success-50/70'
            : 'cursor-pointer hover:bg-gray-50/70',
      )}
      onClick={() => !disabled && onToggle(perm.id, !checked)}
    >
      <div className="mt-0.5">
        <PermCheckbox
          checked={checked}
          onChange={(v) => !disabled && onToggle(perm.id, v)}
          disabled={disabled}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] font-medium leading-tight',
              checked ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500',
            )}
          >
            {perm.key}
          </span>
          <span
            className={cn('text-sm font-semibold', checked ? 'text-gray-900' : 'text-gray-600')}
          >
            {name}
          </span>
        </div>
        {perm.description && (
          <p className="mt-1 text-xs text-gray-400 leading-relaxed">{perm.description}</p>
        )}
      </div>
      {checked && (
        <span className="mt-0.5 shrink-0 inline-flex items-center rounded-full border border-success-200 bg-white px-3 py-1 text-xs font-semibold text-success-600">
          Enabled
        </span>
      )}
    </div>
  );
}

// ── Permission group ──────────────────────────────────────────────────────────

function PermGroup({
  resource,
  perms,
  selected,
  expanded,
  onToggleExpand,
  onToggle,
  onSelectAll,
  disabled,
}: {
  resource: string;
  perms: Permission[];
  selected: Set<string>;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggle: (id: string, v: boolean) => void;
  onSelectAll: (resource: string, select: boolean) => void;
  disabled?: boolean;
}) {
  const meta = getResourceMeta(resource);
  const Icon = meta.icon;
  const selectedCount = perms.filter((p) => selected.has(p.id)).length;
  const total = perms.length;
  const pct = total > 0 ? Math.round((selectedCount / total) * 100) : 0;
  const allSelected = selectedCount === total;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-4 px-5 py-4">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            meta.iconBg,
          )}
        >
          <Icon size={18} className={meta.iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold text-gray-900">{meta.label}</span>
            {disabled ? (
              <span className="inline-flex items-center rounded-full bg-error-50 px-2 py-0.5 text-[10px] font-semibold text-error-600 border border-error-100">
                {isSharjahPerm(resource) ? 'Sharjah Municipality Only' : 'Kalba Municipality Only'}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-success-500 px-2 py-0.5 text-[11px] font-bold text-white">
                {selectedCount}/{total}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex-1 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  disabled ? 'bg-gray-300' : 'bg-success-500',
                )}
                style={{ width: `${disabled ? 0 : pct}%` }}
              />
            </div>
            <span className="text-[11px] font-semibold text-gray-500 w-8 shrink-0">
              {disabled ? '—' : `${pct}%`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onSelectAll(resource, !allSelected)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
              disabled
                ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
          <button
            type="button"
            onClick={onToggleExpand}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {perms.map((p) => (
            <PermRow
              key={p.id}
              perm={p}
              checked={selected.has(p.id)}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const newRoleSchema = z.object({
  name: z.string().min(2, 'Role name must be at least 2 characters').max(50),
  description: z.string().optional(),
  licensingAuthorities: z
    .array(z.enum(['KALBA_MUNICIPALITY', 'SHARJAH_MUNICIPALITY']))
    .min(1, 'Select at least one licensing authority'),
});
type NewRoleValues = z.infer<typeof newRoleSchema>;

function NewRolePage() {
  const navigate = useNavigate();

  const {
    selectedIds,
    search,
    debSearch,
    expanded,
    expandAll,
    setSearch,
    setDebSearch,
    setExpanded,
    setExpandAll,
    togglePermission,
    toggleResource,
    reset: resetStore,
  } = useRoleFormStore();

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<NewRoleValues>({
    resolver: zodResolver(newRoleSchema),
    defaultValues: { name: '', description: '', licensingAuthorities: [] },
  });

  const licensingAuthorities = watch('licensingAuthorities') || [];

  const name = watch('name');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => resetStore();
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebSearch(search), 200);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const { data: allPerms = [] } = useQuery({
    queryKey: ['permissions-all'],
    queryFn: fetchAllPermissions,
  });

  useEffect(() => {
    if (allPerms.length === 0) return;
    const resources = [...new Set(allPerms.map((p) => p.resource))];
    setExpanded(Object.fromEntries(resources.map((r) => [r, true])));
  }, [allPerms.length]);

  useEffect(() => {
    if (expandAll === null || allPerms.length === 0) return;
    const resources = [...new Set(allPerms.map((p) => p.resource))];
    setExpanded(Object.fromEntries(resources.map((r) => [r, expandAll])));
  }, [expandAll]);
  const isAuthorityDisabled = (resource: string) => {
    if (isSharjahPerm(resource) && !licensingAuthorities.includes('SHARJAH_MUNICIPALITY')) {
      return true;
    }
    if (isKalbaPerm(resource) && !licensingAuthorities.includes('KALBA_MUNICIPALITY')) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const hasKalba = licensingAuthorities.includes('KALBA_MUNICIPALITY');
    const hasSharjah = licensingAuthorities.includes('SHARJAH_MUNICIPALITY');
    if (!hasKalba || !hasSharjah) {
      const toClear: string[] = [];
      for (const p of allPerms) {
        if (selectedIds.has(p.id)) {
          if (isKalbaPerm(p.resource) && !hasKalba) {
            toClear.push(p.id);
          } else if (isSharjahPerm(p.resource) && !hasSharjah) {
            toClear.push(p.id);
          }
        }
      }
      if (toClear.length > 0) {
        toggleResource(toClear, false);
      }
    }
  }, [licensingAuthorities, allPerms, selectedIds, toggleResource]);

  const byResource = useMemo(() => {
    const filtered = debSearch
      ? allPerms.filter(
          (p) =>
            p.key.toLowerCase().includes(debSearch.toLowerCase()) ||
            (p.description ?? '').toLowerCase().includes(debSearch.toLowerCase()) ||
            derivePermissionName(p.action, p.resource)
              .toLowerCase()
              .includes(debSearch.toLowerCase()),
        )
      : allPerms;
    return filtered.reduce<Record<string, Permission[]>>((acc, p) => {
      if (!acc[p.resource]) acc[p.resource] = [];
      acc[p.resource].push(p);
      return acc;
    }, {});
  }, [allPerms, debSearch]);

  const [activeTab, setActiveTab] = useState<'general' | 'kalba' | 'sharjah'>('general');

  const resources = Object.keys(byResource).sort();
  const tabResources = resources.filter((r) => (RESOURCE_TAB[r] ?? 'general') === activeTab);
  const totalSelected = selectedIds.size;
  const moduleCount = resources.filter((r) =>
    byResource[r].some((p) => selectedIds.has(p.id)),
  ).length;

  const tabCounts = useMemo(() => {
    const tabs = ['general', 'kalba', 'sharjah'] as const;
    return Object.fromEntries(
      tabs.map((tab) => {
        const perms = allPerms.filter((p) => (RESOURCE_TAB[p.resource] ?? 'general') === tab);
        return [
          tab,
          { selected: perms.filter((p) => selectedIds.has(p.id)).length, total: perms.length },
        ];
      }),
    ) as Record<'general' | 'kalba' | 'sharjah', { selected: number; total: number }>;
  }, [allPerms, selectedIds]);

  const tabPermIds = useMemo(
    () =>
      allPerms
        .filter((p) => (RESOURCE_TAB[p.resource] ?? 'general') === activeTab)
        .map((p) => p.id),
    [allPerms, activeTab],
  );
  const allTabSelected = tabPermIds.length > 0 && tabPermIds.every((id) => selectedIds.has(id));

  function handleSelectAllTab() {
    toggleResource(tabPermIds, !allTabSelected);
  }

  // Resource-level count helpers for Coverage sidebar
  const resourceGroups = useMemo(
    () =>
      Object.entries(
        allPerms.reduce<Record<string, Permission[]>>((acc, p) => {
          if (!acc[p.resource]) acc[p.resource] = [];
          acc[p.resource].push(p);
          return acc;
        }, {}),
      ).sort(([a], [b]) => a.localeCompare(b)),
    [allPerms],
  );

  const createMutation = useMutation({
    mutationFn: async (data: NewRoleValues) => {
      const role = await (rolesControllerCreate({
        requestBody: {
          name: data.name.trim(),
          description: data.description?.trim() || undefined,
          licensingAuthorities: data.licensingAuthorities,
        },
      }) as unknown as Promise<{ id: string }>);

      if (selectedIds.size > 0) {
        await rolesControllerAssignPermissions({
          id: role.id,
          requestBody: { permissionIds: [...selectedIds] },
        });
      }
      return role;
    },
    onSuccess: (role) => {
      navigate({ to: '/users/roles/$roleId', params: { roleId: role.id } });
    },
  });

  function handleToggle(id: string, v: boolean) {
    togglePermission(id, v);
  }

  function handleSelectAll(resource: string, select: boolean) {
    const perms = byResource[resource] ?? [];
    toggleResource(
      perms.map((p) => p.id),
      select,
    );
  }

  const breadcrumbItems = [
    { label: 'Settings' },
    { label: 'Roles', href: '/users/roles' },
    { label: 'Create New Role' },
  ];

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Page header ── */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate({ to: '/users/roles' })}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Create New Role</h1>
            <p className="text-sm text-gray-500 mt-0.5">Define permissions and access control</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/users/roles' })}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={
              !(
                !errors.name &&
                !errors.licensingAuthorities &&
                !!name?.trim() &&
                licensingAuthorities.length > 0
              ) || createMutation.isPending
            }
            onClick={handleSubmit((d) => createMutation.mutate(d))}
          >
            <Shield size={15} />
            {createMutation.isPending ? 'Creating…' : 'Create Role'}
          </Button>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="grid grid-cols-3 gap-6 items-start">
        {/* ── Left: form cards ── */}
        <div className="col-span-2 space-y-5">
          {/* Role Information card */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-success-50">
                <Shield size={17} className="text-success-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-900">Role Information</h2>
                <p className="text-xs text-gray-400 mt-0.5">Name and describe this role</p>
              </div>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Role Name */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-gray-800">
                    Role Name <span className="text-error-500 ml-0.5">*</span>
                  </label>
                  <span className="text-xs text-gray-400">{(name ?? '').length}/50</span>
                </div>
                <input
                  type="text"
                  maxLength={50}
                  placeholder="e.g. Permits Manager, Finance Officer..."
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-success-500/20 focus:border-success-400 transition-all"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-error-500">{errors.name.message}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block mb-1.5 text-sm font-semibold text-gray-800">
                  Description <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="What does this role allow? Who should be assigned to it?"
                  className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-success-500/20 focus:border-success-400 transition-all"
                  {...register('description')}
                />
              </div>
              {/* Licensing Authorities */}
              <div>
                <label className="block mb-1.5 text-sm font-semibold text-gray-800">
                  Licensing Authorities <span className="text-error-500 ml-0.5">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-3">
                  Select the licensing authorities that this role applies to. You must select at
                  least one.
                </p>
                <Controller
                  control={control}
                  name="licensingAuthorities"
                  render={({ field }) => {
                    const values = field.value || [];
                    const handleToggleAuthority = (auth: LicensingAuthority) => {
                      const next = values.includes(auth)
                        ? values.filter((v) => v !== auth)
                        : [...values, auth];
                      field.onChange(next);
                    };

                    return (
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => handleToggleAuthority('KALBA_MUNICIPALITY')}
                          className={cn(
                            'flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left',
                            values.includes('KALBA_MUNICIPALITY')
                              ? 'bg-success-50/50 border-success-300 text-success-800'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                          )}
                        >
                          <div
                            className={cn(
                              'w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-all',
                              values.includes('KALBA_MUNICIPALITY')
                                ? 'bg-success-500 border-success-500'
                                : 'border-gray-300',
                            )}
                          >
                            {values.includes('KALBA_MUNICIPALITY') && (
                              <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                            )}
                          </div>
                          Kalba Municipality
                        </button>

                        <button
                          type="button"
                          onClick={() => handleToggleAuthority('SHARJAH_MUNICIPALITY')}
                          className={cn(
                            'flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left',
                            values.includes('SHARJAH_MUNICIPALITY')
                              ? 'bg-success-50/50 border-success-300 text-success-800'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                          )}
                        >
                          <div
                            className={cn(
                              'w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-all',
                              values.includes('SHARJAH_MUNICIPALITY')
                                ? 'bg-success-500 border-success-500'
                                : 'border-gray-300',
                            )}
                          >
                            {values.includes('SHARJAH_MUNICIPALITY') && (
                              <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                            )}
                          </div>
                          Sharjah Municipality
                        </button>
                      </div>
                    );
                  }}
                />
                {errors.licensingAuthorities && (
                  <p className="mt-1.5 text-xs text-error-500 font-medium">
                    {errors.licensingAuthorities.message}
                  </p>
                )}
              </div>

              {/* Role Type */}
              <div>
                <label className="block mb-3 text-sm font-semibold text-gray-800">Role Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Custom (selected) */}
                  <div className="relative rounded-2xl border-2 border-success-500 bg-success-50/30 p-5 cursor-pointer">
                    <div className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-success-500">
                      <Check size={11} strokeWidth={3} className="text-white" />
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success-100 mb-3">
                      <Sparkles size={18} className="text-success-600" />
                    </div>
                    <p className="text-sm font-bold text-success-600 mb-1">Custom</p>
                    <p className="text-xs text-gray-500">
                      Fully configurable, tailor permissions to your needs
                    </p>
                  </div>

                  {/* System (disabled) */}
                  <div className="rounded-2xl border-2 border-gray-200 bg-white p-5 opacity-60 cursor-not-allowed">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 mb-3">
                      <Lock size={18} className="text-gray-500" />
                    </div>
                    <p className="text-sm font-bold text-gray-700 mb-1">System</p>
                    <p className="text-xs text-gray-400">
                      Managed by the platform, limited customization
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Permission tab selector */}
          <Tabs
            tabs={[
              {
                id: 'general',
                label: 'General',
                badge: `${tabCounts.general.selected}/${tabCounts.general.total}`,
              },
              {
                id: 'kalba',
                label: 'Kalba Municipality',
                badge: `${tabCounts.kalba.selected}/${tabCounts.kalba.total}`,
                disabled: !licensingAuthorities.includes('KALBA_MUNICIPALITY'),
              },
              {
                id: 'sharjah',
                label: 'Sharjah Municipality',
                badge: `${tabCounts.sharjah.selected}/${tabCounts.sharjah.total}`,
                disabled: !licensingAuthorities.includes('SHARJAH_MUNICIPALITY'),
              },
            ]}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'general' | 'kalba' | 'sharjah')}
          />

          {/* Permission Assignment card */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-success-50">
                  <Zap size={17} className="text-success-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">Permission Assignment</h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {totalSelected} permission{totalSelected !== 1 ? 's' : ''} selected across{' '}
                    {moduleCount} module{moduleCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setExpandAll(true)}
                  className="text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Expand all
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  onClick={() => setExpandAll(false)}
                  className="text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Collapse all
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Search + Select all tab row */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search
                    size={14}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search permissions by name, module, or slug..."
                    className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-success-500/20 focus:border-success-400 transition-all placeholder:text-gray-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSelectAllTab}
                  disabled={tabPermIds.length === 0}
                  className="shrink-0 rounded-xl border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                >
                  {allTabSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              {/* Groups */}
              <div className="space-y-3">
                {tabResources.length === 0 ? (
                  <div className="py-10 text-center">
                    <Shield size={32} className="mx-auto mb-3 text-gray-200" />
                    <p className="text-sm text-gray-400">
                      {debSearch ? 'No permissions match your search' : 'Loading permissions…'}
                    </p>
                  </div>
                ) : (
                  tabResources.map((resource) => (
                    <PermGroup
                      key={resource}
                      resource={resource}
                      perms={byResource[resource]}
                      selected={selectedIds}
                      expanded={expanded[resource] ?? true}
                      onToggleExpand={() =>
                        setExpanded({ ...expanded, [resource]: !expanded[resource] })
                      }
                      onToggle={handleToggle}
                      onSelectAll={handleSelectAll}
                      disabled={isAuthorityDisabled(resource)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-4">
          {/* PREVIEW */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">
              Preview
            </p>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-green-700 shadow-sm">
                <Shield size={18} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p
                  className={cn('text-sm font-semibold', name ? 'text-gray-900' : 'text-gray-400')}
                >
                  {name || 'Untitled role'}
                </p>
                <span className="inline-flex mt-0.5 rounded-md bg-success-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Custom
                </span>
              </div>
            </div>
            {watch('description') ? (
              <p className="text-xs text-gray-500 leading-relaxed mb-4">{watch('description')}</p>
            ) : (
              <p className="text-xs text-gray-400 italic mb-4">No description yet.</p>
            )}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{totalSelected}</p>
                <p className="text-xs text-gray-400 mt-0.5">Permissions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{moduleCount}</p>
                <p className="text-xs text-gray-400 mt-0.5">Modules</p>
              </div>
            </div>
            {moduleCount === 0 ? (
              <p className="text-center text-xs text-gray-400 py-2">No modules selected yet</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                {resourceGroups
                  .filter(([, perms]) => perms.some((p) => selectedIds.has(p.id)))
                  .map(([resource]) => {
                    const meta = getResourceMeta(resource);
                    return (
                      <span
                        key={resource}
                        className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-600"
                      >
                        {meta.label}
                      </span>
                    );
                  })}
              </div>
            )}
          </div>

          {/* COVERAGE */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">
              Coverage
            </p>
            <div className="space-y-2.5">
              {resourceGroups.map(([resource, perms]) => {
                const meta = getResourceMeta(resource);
                const Icon = meta.icon;
                const selected = perms.filter((p) => selectedIds.has(p.id)).length;
                return (
                  <div key={resource} className="flex items-center gap-2.5">
                    <Icon size={14} className={meta.iconColor} />
                    <span className="flex-1 text-sm text-gray-700">{meta.label}</span>
                    <span className="text-xs font-semibold text-gray-500">
                      {selected}/{perms.length}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* TIPS */}
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info size={15} className="text-blue-500 shrink-0" />
              <p className="text-sm font-semibold text-blue-700">Tips</p>
            </div>
            <ul className="space-y-1.5 text-xs text-blue-600 leading-relaxed">
              <li>• Use "Clone from" to inherit from an existing role</li>
              <li>• Custom roles can be edited any time after creation</li>
              <li>• Permissions are additive across multiple roles</li>
            </ul>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
